import React, { useEffect } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { BottomNav, DesktopNav } from '@/components/navigation'
import { useAuth } from '@/hooks/useAuth'
import { ProtectedRoute } from '@/components/common/ProtectedRoute'
import { RoleBasedRoute } from '@/components/common/RoleBasedRoute'
import { SuperAdminRoute } from '@/components/common/SuperAdminRoute'

// Import pages
import LoginPage from '@/pages/LoginPage'
import RegisterPage from '@/pages/RegisterPage'
import AffiliateRegisterPage from '@/pages/AffiliateRegisterPage'
import { DiscoverPage } from '@/pages/DiscoverPage'
import { CourseDetailPage } from '@/pages/CourseDetailPage'
import { LessonViewerPage } from '@/pages/LessonViewerPage'
import { CourseFormPage } from '@/pages/CourseFormPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { ClassroomPage } from '@/pages/ClassroomPage'
import { ProfilePage } from '@/pages/ProfilePage'
import { VerifyCertificatePage } from '@/pages/VerifyCertificatePage'
import VerifyEmailPage from '@/pages/VerifyEmailPage'
import VerifyTOTPPage from '@/pages/VerifyTOTPPage'
import AccessDeniedPage from '@/pages/AccessDeniedPage'
import NotFoundPage from '@/pages/NotFoundPage'
import { PaymentHistoryPage } from '@/pages/PaymentHistoryPage'
import { MembershipPage } from '@/pages/MembershipPage'
// import { StudentRefundsPage } from '@/pages/StudentRefundsPage' // HIDDEN — Refund feature disabled

// Import layouts
import { AdminLayout, InstructorLayout, AffiliateLayout } from '@/components/layout'

// Import admin pages
import {
  AdminDashboardPage,
  AdminCoursesPage,
  AdminCourseBuilderPage,
  AdminModuleEditorPage,
  AdminUsersPage,
  AdminBatchesPage,
  AdminBatchRoomsPage,
  AdminWithdrawalsPage,
  AdminCommissionsPage,
  AdminSettingsPage,
  AdminPrivilegeManagementPage,
  AdminSystemSettingsPage,
  AdminKTPReviewPage,
  AdminAttendanceScanPage,
  AdminContentReviewInboxPage,
  AdminAuditLogPage,
  AdminPaymentsPage,
  AdminPaymentDetailPage,
  AdminStaffPage,
  AdminInstructorsPage,
  AdminGradingPage,
  AdminCouponsPage,
  AdminCouponDetailPage,
  AdminMembershipsPage,
  AdminCertificatesPage,
  AdminNotificationsPage,
  AdminSMTPSettingsPage,
} from '@/pages/admin'
import { AdminPaymentVerificationsPage } from '@/pages/AdminPaymentVerificationsPage'
import { AdminPaymentVerificationDetailPage } from '@/pages/AdminPaymentVerificationDetailPage'
import { AdminBankConfigPage } from '@/pages/admin/AdminBankConfigPage'

// M5: invitation claim page (public)
import InvitationClaimPage from '@/pages/InvitationClaimPage'
import { BatchesPage } from '@/pages/BatchesPage'

// Import instructor pages
import {
  InstructorDashboardPage,
  InstructorCoursesPage,
  InstructorModulesPage,
  InstructorCourseDetailPage,
  InstructorModuleEditorPage,
  InstructorEarningsPage,
  InstructorBatchesPage,
  InstructorAttendancePage,
  InstructorAttendanceScanPage,
  InstructorAssignmentSubmissionsPage,
  InstructorGradingPage,
  InstructorQuizReviewPage,
} from '@/pages/instructor'

// Import student pages
import { StudentGradePage } from '@/pages/StudentGradePage'


// Import affiliate pages
import {
  AffiliateDashboardPage,
  AffiliateLinksPage,
  AffiliateTrackingPage,
  AffiliateEarningsPage,
  AffiliateWithdrawPage,
} from '@/pages/affiliate'

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

const Layout: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, user } = useAuth()

  // Check if we're in classroom mode (hide nav).
  // /learn/:enrollmentId/grades is the grade page — it uses its own layout, not the classroom shell.
  const isClassroom =
    location.pathname.startsWith('/learn/') && !location.pathname.endsWith('/grades')

  // Check if we're in admin/instructor/affiliate panel (these have their own layouts)
  const isPanel = location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/instructor') ||
    location.pathname.startsWith('/affiliate')

  // Check if this is the student dashboard (protected student-only page)
  const path = location.pathname
  const isStudentDashboard = path === '/dashboard'

  // Auto-redirect non-student users from student dashboard to their panel
  // Note: Public pages (/, /discover, /course/:id) are accessible to all roles
  const roleRedirects: Record<string, string> = {
    admin: '/admin',
    super_admin: '/admin',
    instructor: '/instructor',
    affiliate: '/affiliate'
  }

  // Listen for auth logout events (must be before any conditional returns)
  useEffect(() => {
    const handleLogout = () => {
      navigate('/login', { replace: true })
    }

    window.addEventListener('auth-logout', handleLogout)
    return () => {
      window.removeEventListener('auth-logout', handleLogout)
    }
  }, [navigate])

  // Listen for auth-email-not-verified and auth-permission-denied events from api.ts.
  useEffect(() => {
    const handleEmailNotVerified = () => {
      if (location.pathname !== '/verify-email') {
        navigate('/verify-email')
      }
    }
    const handlePermissionDenied = () => {
      if (location.pathname !== '/access-denied') {
        navigate('/access-denied')
      }
    }
    window.addEventListener('auth-email-not-verified', handleEmailNotVerified)
    window.addEventListener('auth-permission-denied', handlePermissionDenied)
    return () => {
      window.removeEventListener('auth-email-not-verified', handleEmailNotVerified)
      window.removeEventListener('auth-permission-denied', handlePermissionDenied)
    }
  }, [navigate, location.pathname])

  const shouldRedirectToPanel = isAuthenticated &&
    user &&
    user.role !== 'student' &&
    isStudentDashboard &&
    roleRedirects[user.role]

  if (shouldRedirectToPanel) {
    return <Navigate to={roleRedirects[user.role]} replace />
  }

  // Classroom layout (no navigation)
  if (isClassroom) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route
            path="/learn/:enrollmentId"
            element={
              <ProtectedRoute>
                <ClassroomPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </>
    )
  }

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark font-body text-text-main dark:text-white">
      <ScrollToTop />
      {!isPanel && <DesktopNav />}

      <div className="w-full pb-20 md:pb-0">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />} />
          <Route path="/discover" element={<DiscoverPage />} />
          <Route path="/course/:id" element={<CourseDetailPage />} />
          <Route path="/verify" element={<VerifyCertificatePage />} />
          <Route
            path="/courses/:courseId/lessons/:lessonId"
            element={
              <ProtectedRoute>
                <LessonViewerPage />
              </ProtectedRoute>
            }
          />
          <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
          <Route path="/register" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />} />
          <Route path="/affiliate/register" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <AffiliateRegisterPage />} />

          {/* M5: Invitation claim (public entry; page itself redirects unauthenticated users) */}
          <Route path="/invitations/claim/:token" element={<InvitationClaimPage />} />

          {/* Verify-email and access-denied — both require auth to be meaningful but render standalone. */}
          <Route
            path="/verify-email"
            element={
              <ProtectedRoute>
                <VerifyEmailPage />
              </ProtectedRoute>
            }
          />
          <Route path="/access-denied" element={<AccessDeniedPage />} />

          {/* TOTP verification — after login, before full access */}
          <Route path="/verify-totp" element={<VerifyTOTPPage />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/payments"
            element={
              <ProtectedRoute>
                <PaymentHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/membership"
            element={
              <ProtectedRoute>
                <MembershipPage />
              </ProtectedRoute>
            }
          />
          {/* HIDDEN — Refund feature disabled until admin review page is ready */}
          {/* <Route
            path="/my/refunds"
            element={
              <ProtectedRoute>
                <StudentRefundsPage />
              </ProtectedRoute>
            }
          /> */}
          <Route
            path="/learn/:enrollmentId"
            element={
              <ProtectedRoute>
                <ClassroomPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn/:enrollmentId/grades"
            element={
              <ProtectedRoute>
                <StudentGradePage />
              </ProtectedRoute>
            }
          />


          <Route
            path="/course/:courseId/batches"
            element={
              <ProtectedRoute>
                <BatchesPage />
              </ProtectedRoute>
            }
          />

          {/* Legacy redirects for backward compatibility */}
          <Route path="/courses" element={<Navigate to="/discover" replace />} />
          <Route path="/courses/:id" element={<Navigate to="/course/:id" replace />} />

          {/* Admin Panel Routes */}
          <Route
            path="/admin"
            element={
              <RoleBasedRoute allowedRoles={['admin', 'super_admin']}>
                <AdminLayout />
              </RoleBasedRoute>
            }
          >
            <Route index element={<AdminDashboardPage />} />
            <Route path="courses" element={<AdminCoursesPage />} />
            <Route path="courses/new" element={<CourseFormPage />} />
            <Route path="courses/:id/edit" element={<CourseFormPage />} />
            <Route path="courses/:id/builder" element={<AdminCourseBuilderPage />} />
            <Route path="modules/:moduleId/lessons" element={<AdminModuleEditorPage />} />
            <Route path="assignments" element={<InstructorAssignmentSubmissionsPage />} />
            <Route path="assignments/:assignmentId" element={<InstructorAssignmentSubmissionsPage />} />
            <Route path="quizzes" element={<InstructorQuizReviewPage />} />
            <Route path="quizzes/:quizId" element={<InstructorQuizReviewPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="batches" element={<AdminBatchesPage />} />
            <Route path="batches/:id/rooms" element={<AdminBatchRoomsPage />} />
            <Route path="batches/:batchId/attendance-scan" element={<AdminAttendanceScanPage />} />
            <Route path="ktp-review" element={<AdminKTPReviewPage />} />
            <Route path="withdrawals" element={<AdminWithdrawalsPage />} />
            <Route path="commissions" element={<AdminCommissionsPage />} />
            <Route path="coupons" element={<AdminCouponsPage />} />
            <Route path="coupons/:id" element={<AdminCouponDetailPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            {/* M3 - content authoring review + audit */}
            <Route path="content-review" element={<AdminContentReviewInboxPage />} />
            <Route path="audit-logs" element={<AdminAuditLogPage />} />
            {/* M5 admin refund surfaces */}
            <Route path="payments" element={<AdminPaymentsPage />} />
            <Route path="payments/:id" element={<AdminPaymentDetailPage />} />
            <Route path="payment-verifications" element={<AdminPaymentVerificationsPage />} />
            <Route path="payment-verifications/:id" element={<AdminPaymentVerificationDetailPage />} />
            {/* Instructor management — any admin with user.create can access */}
            <Route path="instructors" element={<AdminInstructorsPage />} />
            {/* Grading scheme management */}
            <Route path="grading" element={<AdminGradingPage />} />
            {/* Membership management */}
            <Route path="memberships" element={<AdminMembershipsPage />} />
            {/* Certificate management */}
            <Route path="certificates" element={<AdminCertificatesPage />} />
            {/* Notification compose */}
            <Route path="notifications/compose" element={<AdminNotificationsPage />} />
            {/* Super-admin only surfaces — client gate (SuperAdminRoute) + backend gate */}
            <Route
              path="staff"
              element={
                <SuperAdminRoute>
                  <AdminStaffPage />
                </SuperAdminRoute>
              }
            />
            <Route
              path="privileges"
              element={
                <SuperAdminRoute>
                  <AdminPrivilegeManagementPage />
                </SuperAdminRoute>
              }
            />
            <Route
              path="system-settings"
              element={
                <SuperAdminRoute>
                  <AdminSystemSettingsPage />
                </SuperAdminRoute>
              }
            />
            <Route
              path="bank-config"
              element={
                <SuperAdminRoute>
                  <AdminBankConfigPage />
                </SuperAdminRoute>
              }
            />
            <Route
              path="smtp-settings"
              element={
                <SuperAdminRoute>
                  <AdminSMTPSettingsPage />
                </SuperAdminRoute>
              }
            />
            <Route path="profile" element={<ProfilePage />} />
          </Route>

          {/* Instructor Panel Routes */}
          <Route
            path="/instructor"
            element={
              <RoleBasedRoute allowedRoles={['instructor']}>
                <InstructorLayout />
              </RoleBasedRoute>
            }
          >
            <Route index element={<InstructorDashboardPage />} />
            <Route path="courses" element={<InstructorCoursesPage />} />
            <Route path="courses/:courseId/modules" element={<InstructorCourseDetailPage />} />
            <Route path="modules" element={<InstructorModulesPage />} />
            <Route path="modules/:moduleId/edit" element={<InstructorModuleEditorPage />} />
            <Route path="assignments" element={<InstructorAssignmentSubmissionsPage />} />
            <Route path="assignments/:assignmentId" element={<InstructorAssignmentSubmissionsPage />} />
            <Route path="earnings" element={<InstructorEarningsPage />} />
            <Route path="batches" element={<InstructorBatchesPage />} />
            <Route path="batches/:batchId/attendance" element={<InstructorAttendancePage />} />
            <Route path="batches/:batchId/attendance-scan" element={<InstructorAttendanceScanPage />} />
            <Route path="grading" element={<InstructorGradingPage />} />
            <Route path="quizzes" element={<InstructorQuizReviewPage />} />
            <Route path="quizzes/:quizId" element={<InstructorQuizReviewPage />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>

          {/* Affiliate Panel Routes */}
          <Route
            path="/affiliate"
            element={
              <RoleBasedRoute allowedRoles={['affiliate']}>
                <AffiliateLayout />
              </RoleBasedRoute>
            }
          >
            <Route index element={<AffiliateDashboardPage />} />
            <Route path="links" element={<AffiliateLinksPage />} />
            <Route path="tracking" element={<AffiliateTrackingPage />} />
            <Route path="earnings" element={<AffiliateEarningsPage />} />
            <Route path="withdraw" element={<AffiliateWithdrawPage />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>

      {!isPanel && <BottomNav />}
    </div>
  )
}

export default Layout